// Direct EHR connection (records-retrieval program): our own replacement for
// the Fasten widget for vendors we're registered with.
//
//   1. POST /health-records/connect   → the portal's sign-in URL
//   2. the patient signs in to their portal (MyChart, ...) in a secure
//      browser sheet; the records API's callback hands code + state back
//   3. POST /health-records/complete  → the server exchanges the code, but
//      only for the same user and plan that started (step 1)
//
// Every route answers 404 unless Admin has direct connect on for this account,
// which is how the screen decides whether to offer it at all.

import { Platform } from 'react-native';
import { apiRecordsGet, apiRecordsPost } from './apiClient';
import { openOAuthSession } from './oauthBrowser';
import { getSessionIds } from '../store/actions/_sessionIds';

export const DIRECT_CONNECT_RETURN = 'com.eleplan.app://health-records-auth';
// Must match mobile/web/oauth/health-records.js.
export const DIRECT_CONNECT_CHANNEL = 'eleplan-health-records-auth';

export interface DirectConnection {
  id: number;
  vendor: string;
  endpoint_id: string;
  org_name: string | null;
  /** The org's own domain, for its logo; null when we can't name one. */
  logo_domain?: string | null;
  /** needs_review: the patient isn't who the plan is for; nothing imported until someone confirms. */
  status: 'pending' | 'active' | 'needs_reauth' | 'needs_review' | 'error';
  /** On a held connection: whose records the provider returned, and which check disagreed. */
  patient_name_full?: string | null;
  patient_hold_dims?: string[] | null;
  last_synced_at: string | null;
  last_error: string | null;
  /** `progress` is present only while a first sync runs. */
  last_counts: { fetched?: number; progress?: { done: number; total: number; current: string } } | null;
  created_at: string;
}

export interface CatalogResult {
  org_id: string;
  name: string;
  city: string | null;
  state: string | null;
  brand: string | null;
  vendor: string;
  endpoint_id: string | null;
  connectable: boolean;
  /** The patient login at this organization (MyChart, healow, ...); null when the vendor's login has no single name. */
  portal?: string | null;
  /** A vendor test sandbox, listed for staff accounts only. */
  sandbox?: boolean;
  /** The org's own domain, for its logo; null when we can't name one. */
  logo_domain?: string | null;
  /** Miles from the plan's (or a typed) ZIP, on nearby results only. */
  miles?: number;
  /** A shorter name for the picker's logo tile ("Jefferson Health"); null when `name` is already the tile name. */
  short_name?: string | null;
  /** How many sites this result stands for (CVS MinuteClinic: one sign-in for 1,993). */
  locations?: number;
  /** A brand on more than one sign-in (Kaiser's regions): one per sign-in, to pick from. */
  choices?: CatalogResult[];
}

/** A national source outside the provider catalog, offered as its own card (Medicare, VA, insurers). */
export interface ConnectSource {
  /** The source's vendor slug: 'medicare', 'va', 'aetna', … */
  kind: string;
  name: string;
  endpoint_id: string;
  sandbox: boolean;
  /** Where the person signs in ("Medicare.gov", "Aetna.com"). */
  portal?: string;
  /** The source's own domain, for its logo. */
  logo_domain?: string;
  /** The picker tile's name ("VA"); a sandbox tile adds a Test badge. */
  short_name?: string | null;
}

/** Sources this account can connect besides doctors and hospitals; [] when none. */
// Organizations patients can connect right now, from the sources call (the
// same count Admin and eleplan.com/connect show). Picker copy reads it.
let connectableOrgs: number | null = null;

export async function fetchConnectSources(): Promise<ConnectSource[]> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/sources/${accountId}/${cognitoId}`);
  if (res?.success && Number(res.connectable_orgs) > 0) connectableOrgs = Number(res.connectable_orgs);
  return res?.success && Array.isArray(res.payload) ? res.payload : [];
}

/** "142,000+": the live connectable count rounded down, or the last published figure before it loads. */
export function providerCountLabel(count: number | null = connectableOrgs): string {
  const n = count && count > 0 ? Math.floor(count / 1000) * 1000 : 142000;
  return `${n.toLocaleString('en-US')}+`;
}

/**
 * "Sign in with your MyChart account", or the generic portal label. "Account"
 * because many hospitals rename the app (MyUCSF is a MyChart account).
 */
export function loginLabel(r: Pick<CatalogResult, 'portal'>): string {
  return r.portal ? `Sign in with your ${r.portal} account` : 'Sign in with your patient portal account';
}

// Things people type that aren't a provider: the portal app's name, their
// insurer, or the doctor's own name. Each gets a pointer to what to search.
const PORTAL_APPS: Array<[RegExp, string]> = [
  [/\bmy\s?chart\b/, 'MyChart'],
  [/\bhealow\b/, 'healow'],
  [/\bfollow\s?my\s?health\b/, 'FollowMyHealth'],
  [/\bpatient\s?fusion\b/, 'Patient Fusion'],
  [/\bpatient\s+portal\b/, 'A patient portal'],
];
const INSURERS: Array<[RegExp, string]> = [
  [/\bunited\s?health\s?care\b|\buhc\b/, 'UnitedHealthcare'],
  [/\baetna\b/, 'Aetna'],
  [/\bcigna\b/, 'Cigna'],
  [/\bhumana\b/, 'Humana'],
  [/\banthem\b/, 'Anthem'],
  [/\bblue\s?cross\b|\bblue\s?shield\b|\bbcbs\b/, 'Blue Cross Blue Shield'],
  [/\bmedicare\b/, 'Medicare'],
  [/\bmedicaid\b/, 'Medicaid'],
  [/\btricare\b/, 'TRICARE'],
];

/**
 * Guidance for a search that's probably not a provider name, else null. The
 * wording stays true when real results match too ("Cigna Medical Group"), and
 * the doctor-name hint only shows when nothing matched ("Dr. Phillips Hospital").
 */
export function searchHint(query: string, hasResults = false, medicareAvailable = false): string | null {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return null;
  for (const [re, name] of PORTAL_APPS) {
    if (re.test(q)) {
      return `${name} is a sign-in app many hospitals and practices use. To find yours, search the provider's name shown on its sign-in screen or on a bill.`;
    }
  }
  if (medicareAvailable && /\bmedicare\b/.test(q)) {
    return 'Medicare has its own option. Go back and choose Medicare to connect the visits, prescriptions and coverage Medicare paid for.';
  }
  for (const [re, name] of INSURERS) {
    if (re.test(q)) {
      return `${name} is an insurer. To connect medical records, search the doctor's office, clinic or hospital where you get care.`;
    }
  }
  if (!hasResults && /^(dr\.?|doctor)\s/.test(q)) {
    return "Doctors are listed under their practice or hospital. Search that name instead; it's on your appointment reminder or bill.";
  }
  return null;
}

function ids() {
  const { accountId, cognitoId } = getSessionIds();
  return { accountId, cognitoId };
}

/** null = direct connect isn't available to this account (route is 404). */
export async function fetchDirectConnections(projectId: number | string): Promise<DirectConnection[] | null> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/connections/${projectId}/${accountId}/${cognitoId}`);
  if (res?.success && Array.isArray(res.payload)) return res.payload;
  return null;
}

/** A Fasten connection Eleplan Connect can take over: sign in to `endpoint_id` and the Fasten one retires once it syncs. */
export interface FastenMove {
  org_connection_id: string;
  endpoint_id: string;
  name: string;
  portal?: string | null;
  logo_domain?: string | null;
}

/** The plan's Fasten connections Eleplan Connect reaches directly; [] when none or unavailable. */
export async function fetchFastenMoves(projectId: number | string): Promise<FastenMove[]> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/connections/${projectId}/${accountId}/${cognitoId}?fasten=1`);
  return res?.success && Array.isArray(res.fasten_reconnect) ? res.fasten_reconnect : [];
}

/** A provider the plan's insurance claims name (seen-providers.js), with how to connect it. */
export interface SeenProvider {
  name: string;
  short_name?: string | null;
  kind?: 'org' | 'person';
  npi?: string | null;
  /** Visit days (claims and visits). */
  claims: number;
  /** Pharmacy fill days. */
  fills?: number;
  last_seen: string | null;
  matched: boolean;
  endpoint_id: string | null;
  portal?: string | null;
  logo_domain?: string | null;
  connectable: boolean;
  connected: boolean;
}

/** Where the plan's care recipient has been seen, from their claims, visits and fills; [] when none or unavailable. */
export async function fetchSeenProviders(projectId: number | string): Promise<SeenProvider[]> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/connections/${projectId}/${accountId}/${cognitoId}?seen=1`);
  return res?.success && Array.isArray(res.seen_providers) ? res.seen_providers : [];
}

/**
 * Catalog search. A blank `q` with a plan lists connectable providers near
 * that plan's care recipient; a 5-digit `q` lists those near that ZIP.
 */
export async function searchProviders(q: string, projectId?: number | string): Promise<CatalogResult[]> {
  const { accountId, cognitoId } = ids();
  const plan = projectId != null ? `&project_id=${encodeURIComponent(String(projectId))}` : '';
  // grouped=1: one result per brand, its sign-ins as `choices` (the picker opens them).
  const res = await apiRecordsGet(`/health-records/search/${accountId}/${cognitoId}?q=${encodeURIComponent(q)}${plan}&grouped=1`);
  return res?.success && Array.isArray(res.payload) ? res.payload : [];
}

/** Brands people use anywhere (CVS MinuteClinic, Kaiser Permanente), for the picker's Nationwide row. */
export async function fetchNationalProviders(): Promise<CatalogResult[]> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/search/${accountId}/${cognitoId}?national=1`);
  return res?.success && Array.isArray(res.payload) ? res.payload : [];
}

/** One page of every provider patients can connect, A to Z (the picker's "View all"); `total` is the whole list's length; null when it fails. */
export async function browseProviders(offset: number, limit = BROWSE_PAGE): Promise<{ rows: CatalogResult[]; total: number } | null> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsGet(`/health-records/search/${accountId}/${cognitoId}?all=1&offset=${offset}&limit=${limit}`);
  return res?.success && Array.isArray(res.payload) ? { rows: res.payload, total: Number(res.total) || 0 } : null;
}
export const BROWSE_PAGE = 100;

// The picker opens on these lists. Nearby takes about 2 s when the records
// API has to load its provider catalog (most first opens), so the Health Data
// screen starts them early and the picker reuses them; one fetch per plan for
// a few minutes.
const PICKER_LISTS_TTL_MS = 5 * 60 * 1000;
type PickerLists = { nearby: CatalogResult[]; national: CatalogResult[]; sources: ConnectSource[] };
const pickerListsCache = new Map<string, { at: number; lists: Promise<PickerLists> }>();

export function pickerLists(projectId?: number | string): Promise<PickerLists> {
  const { accountId } = ids();
  const key = `${accountId}:${projectId ?? ''}`;
  const hit = pickerListsCache.get(key);
  if (hit && Date.now() - hit.at < PICKER_LISTS_TTL_MS) return hit.lists;
  const lists = Promise.all([
    projectId != null ? searchProviders('', projectId).catch(() => [] as CatalogResult[]) : Promise.resolve([] as CatalogResult[]),
    fetchConnectSources().catch(() => [] as ConnectSource[]),
    fetchNationalProviders().catch(() => [] as CatalogResult[]),
  ]).then(([nearby, sources, national]) => {
    // Nothing came back (offline, an error): try again on the next open.
    if (!nearby.length && !sources.length && !national.length) pickerListsCache.delete(key);
    return { nearby, national, sources };
  });
  pickerListsCache.set(key, { at: Date.now(), lists });
  return lists;
}

export type ConnectOutcome =
  | { ok: true; connectionId: number }
  /** `attestation`: the statement to confirm before this sign-in can start. */
  | { ok: false; cancelled?: boolean; attestation?: string; message: string };

function stateOf(authorizeUrl: string): string | undefined {
  try {
    return new URL(authorizeUrl).searchParams.get('state') || undefined;
  } catch (_) {
    return undefined;
  }
}

/** Parse the return URL the callback (or the web bridge) produced. */
export function parseReturnUrl(url: string | undefined): Record<string, string> {
  if (!url) return {};
  const q = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  return Object.fromEntries(new URLSearchParams(q).entries());
}

export async function connectProvider(
  projectId: number | string,
  endpointId: string,
  signal?: AbortSignal,
  attested = false,
): Promise<ConnectOutcome> {
  const { accountId, cognitoId } = ids();
  const web = Platform.OS === 'web' && typeof window !== 'undefined';
  // On web the server returns the popup through a page on this origin (the
  // server only accepts our own app origins).
  const started = await apiRecordsPost(`/health-records/connect/${projectId}/${accountId}/${cognitoId}`, {
    endpoint_id: endpointId,
    ...(web ? { return_origin: window.location.origin } : {}),
    ...(attested ? { attestation: true } : {}),
  });
  // On a plan for someone else the source may ask the caller to confirm whose
  // account this is first; the caller shows the statement and starts again.
  if (!attested && started?.status === 409 && typeof started.attestation === 'string') {
    return { ok: false, attestation: started.attestation, message: started.attestation };
  }
  const authorizeUrl = started?.payload?.authorize_url;
  if (!started?.success || !authorizeUrl) {
    return { ok: false, message: started?.message || 'Could not start the connection.' };
  }

  const expectedState = stateOf(authorizeUrl);
  const result = await openOAuthSession(authorizeUrl, DIRECT_CONNECT_RETURN, {
    preferEphemeralSession: true,
    ...(web && expectedState ? { bridgeChannel: DIRECT_CONNECT_CHANNEL, expectedState, signal } : {}),
  });
  if (result.type === 'cancel') return { ok: false, cancelled: true, message: 'Sign-in was cancelled.' };
  const params = parseReturnUrl(result.url);
  if (params.status !== 'authorized' || !params.code || !params.state) {
    return { ok: false, message: params.reason === 'access_denied' ? 'Access was not granted.' : "The sign-in didn't finish. Try again." };
  }

  const done = await apiRecordsPost(`/health-records/complete/${projectId}/${accountId}/${cognitoId}`, {
    code: params.code,
    state: params.state,
    ...(params.iss ? { iss: params.iss } : {}),
  });
  if (!done?.success) return { ok: false, message: done?.message || 'Could not finish the connection.' };
  return { ok: true, connectionId: done.payload.connection_id };
}

export async function disconnectProvider(projectId: number | string, connectionId: number): Promise<boolean> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsPost(`/health-records/disconnect/${projectId}/${accountId}/${cognitoId}`, { connection_id: connectionId });
  return Boolean(res?.success);
}

/** Confirm a held connection's patient is the right person; it syncs again. */
export async function acceptConnectionPatient(projectId: number | string, connectionId: number): Promise<boolean> {
  const { accountId, cognitoId } = ids();
  const res = await apiRecordsPost(`/health-records/disconnect/${projectId}/${accountId}/${cognitoId}`, { connection_id: connectionId, accept_patient: true });
  return Boolean(res?.success);
}
